<template>
    <div class="RPSettingsLayout">
        <!--
        Triggered when user requests navigation to a specified page.
        This could come from the Topbar, Breadcrumbs, or Sidebar

        @event navigate
        @property {object} to The Vue router object or other flag used to trigger navigation
    -->
        <RPTopbar
            :tabs="tabs"
            :sections="sections"
            :active-tab="activeTab"
            :dynamic-tab="dynamicTab"
            :status="status"
            class="RPSettingsLayout__topbar"
            @navigate="to => $emit('navigate', to)"
        />
        <SettingsHeader
            class="RPSettingsLayout__header"
            :header="header"
            :breadcrumbs="breadcrumbs"
            :status="status"
            @navigate="to => $emit('navigate', to)"
        >
            <template v-slot:subheader>
                <slot name="subheader"></slot>
            </template>
            <template v-slot:header>
                <slot name="header"></slot>
            </template>
        </SettingsHeader>
        <RPSidebar
            :tabs="tabs"
            :active-tab="activeTab"
            :sections="sections"
            :status="status"
            :dynamic-tab="dynamicTab"
            class="RPSettingsLayout__sidebar"
            @navigate="to => $emit('navigate', to)"
        />

        <!-- @slot Default slot used to contain main Settings Layout content-->
        <slot />
    </div>
</template>
<script>
import RPSidebar from 'components/Complex/Navs/RPSidebar.vue';
import RPTopbar from 'components/Complex/Navs/RPTopbar.vue';
import stat from 'status/index';
import SettingsHeader from './SettingsLayout/SettingsHeader.vue';
import {has} from 'lodash';

export default {
    name: "RPSettingsLayout",
    components: {
        RPSidebar,
        RPTopbar,
        SettingsHeader
    },
    props: {
        /**
        * The header for the page ex) Competitor Location Settings
        */
        header: {
            type: String,
            default: '',
        },
        /**
        * * Breadcrumbs for nested navigation.  Every object in the array must be formatted {to: {name: '<Route Name>', label: '<Route Label>'}},
        * except the final object which is not an active link.  The `to` is used by the Vuex Router,
        * so could also include any relevant params or querys, but must at least include the route name
        * as specified by the routes.js file.  The `label` is what is displayed in breadcrumb.
        * */
        breadcrumbs: {
            type: Array,
            default: () => ([]),
        },
        /**
        * Primary navigation object.  Every object in the array must be formatted {to: {name: '<Route Name>', label: '<Route Label>'}}.
        * The `to` is used by the Vuex Router so could also include any relevant params or queries, but must at least include the route name
        * as specified by the routes.js file.  The `label` is what is displayed in NavTab.
        */
        tabs: {
            type: Array,
            default: () => ([]),
        },
        sections: {
            type: Array,
            default: () => ([]),
        },
        /**
        * Route name of the current active tab typically $route.name
        */
        activeTab: {
            type: String,
            required: true,
        },
        /**
        * Dynamic NavTab label.  Appears below active tab, used to specificy dynamic page values, such a current brand name.  Not an active link
        */
        dynamicTab: {
            type: String,
            default: '',
        },
        /**
        * Component Status (see status/index.js for list of statuses)
        */
        status: {
            type: String,
            default: stat.STANDBY,
        },
    },
    computed: {
        computedHeader(){
            if(this.header !== ''){
                return this.header;
            }
            const tabHeader = this.findActiveTab(this.tabs);
            if(tabHeader !== ''){
                return tabHeader;
            }
            const sectionHeader = this.sections
                .map(section => this.findActiveTab(section.tabs))
                .find(header => header !== '');
            if(sectionHeader){
                return sectionHeader;
            }
            return '';
        }
    },
    methods: {
        findActiveTab(tabArray){
            tabArray.map(tab => {
                if((tab.to.name === this.activeTab)
                    || (has(tab, 'subTabs')
                        && tab.subTabs.find(sub => sub.to.name === this.activeTab))){
                    return tab.label;
                }
            });
            return '';
        }
    }
};
</script>
<style lang="scss">
    .RPSettingsLayout {
        height: 100%;
        display: grid;
        @media(min-width: $max-md-width){
            grid-template-rows: min-content min-content auto;
            grid-template-columns: 1fr 5fr;
            grid-gap: 1rem;
            &__topbar {
                display: none;
            }
            &__header{
                grid-column: span 2;
            }
            &__sidebar{
                grid-column: 1;
                grid-row: 2;
            }
            &__content {
                grid-column: 2;
                grid-row: 2 / span 2;
            }
        }
        @media(max-width: $max-md-width){
            grid-template-columns: auto;
            grid-auto-rows: min-content;
            grid-gap: 2rem;
            &__sidebar {
                display: none;
            }
        }
    }
</style>

<docs>
    ```[import](./examples/RPSettingsLayoutExample.vue)
    ```
</docs>
